/**
 * Episode Detail View Component - mirrors ReportDetailView pattern
 * Shows detailed view of individual podcast episode with transcript and actions
 */
import React, { useState } from 'react';
import { useToast } from '../common/Toast';
import podcastService from '../../services/podcastService';
import { formatDate } from '../../utils/formatters';

const EpisodeDetailView = ({ episode, podcast, onBack, onUpdate }) => {
  const { showToast } = useToast();
  const [loading, setLoading] = useState(false);
  const [currentEpisode, setCurrentEpisode] = useState(episode);
  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState({
    episode_title: episode.episode_title || '',
    episode_description: episode.episode_description || '',
    transcript_text: episode.transcript_text || '',
    special_instructions: episode.special_instructions || ''
  });

  const handleTranscribe = async (forceRetranscribe = false) => {
    try {
      setLoading(true);
      showToast('Transcribing episode audio...', 'info');
      
      const response = await podcastService.transcribeEpisode(currentEpisode.id, forceRetranscribe);
      
      if (response && response.success) {
        const transcriptLength = response.transcript_length || response.data?.transcript_length || 0;
        showToast(`✅ Episode transcribed successfully! (${transcriptLength} characters)`, 'success');
        
        const updatedEpisode = {
          ...currentEpisode,
          transcription_completed: true,
          transcript_text: response.transcript || response.data?.transcript || '',
          transcription_status: 'completed',
          status: 'completed'
        };
        setCurrentEpisode(updatedEpisode);
        
        if (onUpdate) {
          onUpdate(updatedEpisode);
        }
      } else {
        const errorMsg = response?.error || response?.message || 'Transcription failed';
        
        // Special handling for file size errors
        if (errorMsg.includes('too large') || errorMsg.includes('25MB')) {
          showToast(`❌ Audio file too large: ${errorMsg}`, 'error');
        } else {
          showToast(`❌ Transcription failed: ${errorMsg}`, 'error');
        }
      }
    } catch (error) {
      console.error('Error transcribing episode:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Unknown error';
      showToast(`❌ Transcription error: ${errorMessage}`, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateArticle = async () => {
    try {
      setLoading(true);
      showToast('Generating article from episode transcript...', 'info');
      
      // This will need to be implemented - similar to report article generation
      // For now, we'll show a placeholder
      showToast('🚧 Article generation from episodes coming soon!', 'info');
      
      // TODO: Implement episode article generation
      // const response = await podcastService.generateArticleFromEpisode(currentEpisode.id);
      
    } catch (error) {
      console.error('Error generating article:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Unknown error';
      showToast(`❌ Article generation error: ${errorMessage}`, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this episode? This action cannot be undone.')) {
      return;
    }

    try {
      setLoading(true);
      showToast('Deleting episode...', 'info');
      
      const response = await podcastService.deleteEpisode(currentEpisode.id);
      
      if (response && response.success) {
        showToast('✅ Episode deleted successfully!', 'success');
        onBack();
      } else {
        const errorMsg = response?.error || response?.message || 'Delete failed';
        showToast(`❌ Delete failed: ${errorMsg}`, 'error');
      }
    } catch (error) {
      console.error('Error deleting episode:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Unknown error';
      showToast(`❌ Delete error: ${errorMessage}`, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveEdit = async () => {
    try {
      setLoading(true);
      showToast('Saving changes...', 'info');
      
      const response = await podcastService.updateEpisode(currentEpisode.id, editData);
      
      if (response && response.success) {
        showToast('✅ Episode updated successfully!', 'success');
        
        const updatedEpisode = { ...currentEpisode, ...editData };
        setCurrentEpisode(updatedEpisode);
        setIsEditing(false);
        
        if (onUpdate) {
          onUpdate(updatedEpisode);
        }
      } else {
        const errorMsg = response?.error || response?.message || 'Update failed';
        showToast(`❌ Update failed: ${errorMsg}`, 'error');
      }
    } catch (error) {
      console.error('Error updating episode:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Unknown error';
      showToast(`❌ Update error: ${errorMessage}`, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleCancelEdit = () => {
    setEditData({
      episode_title: currentEpisode.episode_title || '',
      episode_description: currentEpisode.episode_description || '',
      transcript_text: currentEpisode.transcript_text || '',
      special_instructions: currentEpisode.special_instructions || ''
    });
    setIsEditing(false);
  };

  const formatDuration = (seconds) => {
    return podcastService.formatDuration(seconds);
  };

  const formatFileSize = (bytes) => {
    return podcastService.formatFileSize(bytes);
  };

  return (
    <div className="h-full flex flex-col bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-4 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <button
              onClick={onBack}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <div>
              <h1 className="text-lg font-semibold text-gray-900">{currentEpisode.episode_title}</h1>
              <p className="text-sm text-gray-600">
                {podcast?.podcast_name} • {formatDuration(currentEpisode.duration_seconds)}
              </p>
            </div>
          </div>
          
          <div className="flex items-center space-x-3">
            {!isEditing && (
              <>
                {!currentEpisode.transcription_completed && (
                  <button
                    onClick={() => handleTranscribe(false)}
                    disabled={loading}
                    className="bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 disabled:bg-purple-400 transition-colors flex items-center space-x-2"
                  >
                    {loading ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b border-white"></div>
                        <span>Transcribing...</span>
                      </>
                    ) : (
                      <>
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15.536 6.464a5 5 0 010 7.072M12 9v6m3 0a9 9 0 01-6 0m6 0v1a2 2 0 01-2 2H9a2 2 0 01-2-2v-1m6 0V9a2 2 0 00-2-2H9a2 2 0 00-2 2v0" />
                        </svg>
                        <span>Transcribe</span>
                      </>
                    )}
                  </button>
                )}

                {currentEpisode.transcription_completed && (
                  <>
                    <button
                      onClick={() => handleTranscribe(true)}
                      disabled={loading}
                      className="bg-purple-100 text-purple-700 px-4 py-2 rounded-lg hover:bg-purple-200 disabled:bg-purple-50 transition-colors flex items-center space-x-2"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                      </svg>
                      <span>Re-transcribe</span>
                    </button>

                    <button
                      onClick={handleGenerateArticle}
                      disabled={loading}
                      className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 disabled:bg-green-400 transition-colors flex items-center space-x-2"
                    >
                      {loading ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b border-white"></div>
                          <span>Generating...</span>
                        </>
                      ) : (
                        <>
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                          </svg>
                          <span>{currentEpisode.article_drafted ? 'Regenerate Article' : 'Generate Article'}</span>
                        </>
                      )}
                    </button>
                  </>
                )}
                
                <button
                  onClick={() => setIsEditing(true)}
                  className="bg-gray-100 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  Edit
                </button>
                
                <button
                  onClick={handleDelete}
                  className="bg-red-100 text-red-700 px-4 py-2 rounded-lg hover:bg-red-200 transition-colors"
                >
                  Delete
                </button>
              </>
            )}
            
            {isEditing && (
              <>
                <button
                  onClick={handleSaveEdit}
                  disabled={loading}
                  className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 disabled:bg-green-400 transition-colors"
                >
                  {loading ? 'Saving...' : 'Save'}
                </button>
                
                <button
                  onClick={handleCancelEdit}
                  className="bg-gray-100 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  Cancel
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto">
        <div className="max-w-6xl mx-auto p-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            
            {/* Main Content */}
            <div className="lg:col-span-2 space-y-6">
              
              {/* Basic Information */}
              <div className="bg-white border border-gray-200 rounded-lg p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">Episode Information</h2>
                
                {isEditing ? (
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Episode Title</label>
                      <input
                        type="text"
                        value={editData.episode_title}
                        onChange={(e) => setEditData({...editData, episode_title: e.target.value})}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                      <textarea
                        value={editData.episode_description}
                        onChange={(e) => setEditData({...editData, episode_description: e.target.value})}
                        rows={3}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Special Instructions</label>
                      <textarea
                        value={editData.special_instructions}
                        onChange={(e) => setEditData({...editData, special_instructions: e.target.value})}
                        rows={2}
                        placeholder="Special instructions for AI processing..."
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                      />
                    </div>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="font-medium text-gray-700">Episode Title:</span>
                      <p className="text-gray-900 mt-1">{currentEpisode.episode_title}</p>
                    </div>
                    <div>
                      <span className="font-medium text-gray-700">Duration:</span>
                      <p className="text-gray-900 mt-1">{formatDuration(currentEpisode.duration_seconds)}</p>
                    </div>
                    <div>
                      <span className="font-medium text-gray-700">Published:</span>
                      <p className="text-gray-900 mt-1">{formatDate(currentEpisode.published_at)}</p>
                    </div>
                    <div>
                      <span className="font-medium text-gray-700">File Size:</span>
                      <p className="text-gray-900 mt-1">{formatFileSize(currentEpisode.file_size_bytes)}</p>
                    </div>
                    <div>
                      <span className="font-medium text-gray-700">Status:</span>
                      <p className="text-gray-900 mt-1 capitalize">{currentEpisode.status}</p>
                    </div>
                    <div>
                      <span className="font-medium text-gray-700">Transcription:</span>
                      <p className="text-gray-900 mt-1 capitalize">{currentEpisode.transcription_status}</p>
                    </div>
                    
                    {currentEpisode.episode_url && (
                      <div className="md:col-span-2">
                        <span className="font-medium text-gray-700">Episode URL:</span>
                        <p className="text-gray-900 mt-1">
                          <a href={currentEpisode.episode_url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800">
                            {currentEpisode.episode_url}
                          </a>
                        </p>
                      </div>
                    )}
                    
                    {currentEpisode.episode_description && (
                      <div className="md:col-span-2">
                        <span className="font-medium text-gray-700 block mb-2">Description:</span>
                        <p className="text-gray-900 text-sm">{currentEpisode.episode_description}</p>
                      </div>
                    )}
                    
                    {currentEpisode.special_instructions && (
                      <div className="md:col-span-2">
                        <span className="font-medium text-gray-700 block mb-2">Special Instructions:</span>
                        <p className="text-gray-900 text-sm">{currentEpisode.special_instructions}</p>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Audio Player */}
              {currentEpisode.audio_url && (
                <div className="bg-white border border-gray-200 rounded-lg p-6">
                  <h2 className="text-lg font-semibold text-gray-900 mb-4">Audio Player</h2>
                  <audio controls className="w-full">
                    <source src={currentEpisode.audio_url} type="audio/mpeg" />
                    Your browser does not support the audio element.
                  </audio>
                  <div className="mt-2 text-xs text-gray-500">
                    Audio file • {formatDuration(currentEpisode.duration_seconds)}
                  </div>
                </div>
              )}

              {/* Transcript Content */}
              {currentEpisode.transcription_completed && currentEpisode.transcript_text && (
                <div className="bg-white border border-gray-200 rounded-lg p-6">
                  <h2 className="text-lg font-semibold text-gray-900 mb-4">Episode Transcript</h2>
                  
                  {isEditing ? (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Transcript</label>
                      <textarea
                        value={editData.transcript_text}
                        onChange={(e) => setEditData({...editData, transcript_text: e.target.value})}
                        rows={20}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm font-mono"
                      />
                    </div>
                  ) : (
                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 max-h-96 overflow-y-auto">
                      <pre className="text-sm text-gray-800 whitespace-pre-wrap font-mono">
                        {currentEpisode.transcript_text}
                      </pre>
                    </div>
                  )}
                  
                  <div className="mt-2 text-xs text-gray-500">
                    Transcript • {currentEpisode.transcript_text.length} characters
                  </div>
                </div>
              )}

              {/* No Transcript State */}
              {!currentEpisode.transcription_completed && (
                <div className="bg-white border border-gray-200 rounded-lg p-6">
                  <div className="text-center py-8">
                    <svg className="w-16 h-16 mb-4 mx-auto text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1" d="M15.536 6.464a5 5 0 010 7.072M12 9v6m3 0a9 9 0 01-6 0m6 0v1a2 2 0 01-2 2H9a2 2 0 01-2-2v-1m6 0V9a2 2 0 00-2-2H9a2 2 0 00-2 2v0" />
                    </svg>
                    <h3 className="text-lg font-medium mb-2 text-gray-900">No Transcript Available</h3>
                    <p className="text-gray-600 mb-4">Click "Transcribe" to generate a transcript from the audio file.</p>
                  </div>
                </div>
              )}
            </div>

            {/* Right Sidebar */}
            <div className="space-y-6">
              
              {/* Processing Status */}
              <div className="bg-white border border-gray-200 rounded-lg p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Processing Status</h3>
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-700">Transcribed</span>
                    {currentEpisode.transcription_completed ? (
                      <span className="text-green-600 text-lg">✓</span>
                    ) : (
                      <span className="text-red-500 text-lg">✗</span>
                    )}
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-700">Article Drafted</span>
                    {currentEpisode.article_drafted ? (
                      <span className="text-green-600 text-lg">✓</span>
                    ) : (
                      <span className="text-red-500 text-lg">✗</span>
                    )}
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-700">WordPress Pushed</span>
                    {currentEpisode.wordpress_pushed ? (
                      <span className="text-green-600 text-lg">✓</span>
                    ) : (
                      <span className="text-red-500 text-lg">✗</span>
                    )}
                  </div>
                </div>
              </div>

              {/* AI Tags */}
              <div className="bg-white border border-gray-200 rounded-lg p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">AI Tags</h3>
                <div className="space-y-3">
                  {currentEpisode.ai_tags && currentEpisode.ai_tags.length > 0 ? (
                    <div className="flex flex-wrap gap-2">
                      {currentEpisode.ai_tags.map((tag, index) => (
                        <span key={index} className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                          {tag}
                        </span>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500">No AI tags generated yet</p>
                  )}
                </div>
              </div>

              {/* Location Tags */}
              <div className="bg-white border border-gray-200 rounded-lg p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Location Tags</h3>
                <div className="space-y-3">
                  {currentEpisode.location_tags && currentEpisode.location_tags.length > 0 ? (
                    <div className="flex flex-wrap gap-2">
                      {currentEpisode.location_tags.map((location, index) => (
                        <span key={index} className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          {location}
                        </span>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500">No location tags identified yet</p>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EpisodeDetailView;